import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { AdminUserPage } from '../_shared/types';
import { toAdminUserPage } from '../_shared/wire';

/**
 * The account roster.
 *
 * **Two parameters are sent, and each has a control that produces it**: the role
 * facet, and `query` — `backend-auth`'s own free-text lookup over name, handle
 * and number. The register is where a block, a role grant, an entitlement and a
 * case history are reached from, so «find this account» is the question the
 * surface exists to answer and paging to it by hand was the only way to ask.
 * The block-state facet stays unsent: no control produces it.
 *
 * The lookup runs **on the service**, over the whole register rather than over
 * the twenty rows in hand — a term filtered client-side would answer «لا حساب»
 * about an account sitting on page four.
 *
 * **No `page_size` is sent.** The page size is the service's, capped at its own
 * bound, and a number chosen here would put a server-owned value behind a
 * rebuild of this bundle. The page comes back declaring the size it used and the
 * total it drew from, and the pager divides one by the other rather than
 * assuming either.
 *
 * `current_page` **is** sent, and it is the page the operator walked to: the
 * register is offset-paged and pinning it to page one made every account past
 * the first page unreachable from the console — the roster is where a block, a
 * role grant, and a case history are reached from, so an unreachable row is an
 * account nobody can act on.
 *
 * `current_page` is snake_case because `backend-auth` spells it so in its own
 * filter model. The console follows the callee.
 */
export function adminUsersKey(role: string | null, page: number, term = '') {
  return key('auth', 'adminUsers', { role, page, term });
}

export function useAdminUsers(role: string | null, page: number, term = '') {
  const trimmed = term.trim();

  return useQuery({
    queryKey: adminUsersKey(role, page, trimmed),
    queryFn: () => {
      const params = new URLSearchParams({ current_page: String(page) });
      if (role) params.set('role', role);
      // `query` is the parameter `backend-auth` registers; the console follows
      // the callee here as it does for `current_page`.
      if (trimmed) params.set('query', trimmed);
      // Mapped, never cast: the service spells the role `role_name` and this
      // bundle reads `role`, and a cast let that mismatch render twenty empty
      // chips without anything throwing.
      return api
        .get<Parameters<typeof toAdminUserPage>[0]>('auth', `/admin/users?${params.toString()}`)
        .then(toAdminUserPage) satisfies Promise<AdminUserPage>;
    },
    // An operator returning to the tab wants the roster as it is now.
    staleTime: STALE_TIME.list,
    // Applying a facet — or walking a page — keeps the previous rows on screen
    // while the next set loads, so the table does not blink through a skeleton
    // on every «طبّق» and every «التالي».
    placeholderData: keepPreviousData,
  });
}
